import { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { supabase } from '@/lib/supabase';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent, CardDescription, CardHeader, CardTitle, CardFooter } from '@/components/ui/card';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Checkbox } from '@/components/ui/checkbox';
import { Label } from '@/components/ui/label';
import { Loader2, Mail, CheckCircle, AlertTriangle, KeyRound } from 'lucide-react';

interface AuthFormProps {
    defaultTab?: 'signin' | 'register';
    title?: string;
    description?: string;
    onSuccess?: (message: string) => void;
    redirectTo?: string;
}

type AuthMode = 'signin' | 'register';
type FieldErrors = {
    email?: string;
    password?: string;
    terms?: string;
    healthConsent?: string;
};

const RESEND_COOLDOWN_SECONDS = 30;
const GENERIC_SIGN_IN_MESSAGE = 'If an account exists for this email, you will receive a sign-in link.';
const REGISTER_SUCCESS_MESSAGE = 'Check your email to finish creating your account.';

export function AuthForm({
    defaultTab = 'signin',
    title = 'Adaptive Endurance',
    description = 'Adaptive Training Plans',
    onSuccess,
    redirectTo
}: AuthFormProps) {
    const [activeTab, setActiveTab] = useState(defaultTab);
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [usePassword, setUsePassword] = useState(false);
    const [termsAccepted, setTermsAccepted] = useState(false);
    const [marketingOptIn, setMarketingOptIn] = useState(false);
    const [healthConsent, setHealthConsent] = useState(false);
    const [loading, setLoading] = useState(false);
    const [authMessage, setAuthMessage] = useState<string | null>(null);
    const [authMessageContext, setAuthMessageContext] = useState<AuthMode | null>(null);
    const [fieldErrors, setFieldErrors] = useState<FieldErrors>({});
    const [resendCooldown, setResendCooldown] = useState(0);
    const [error, setError] = useState<string | null>(null);
    const emailSignInRef = useRef<HTMLInputElement>(null);
    const emailRegisterRef = useRef<HTMLInputElement>(null);
    const passwordRef = useRef<HTMLInputElement>(null);

    const redirectUrl = redirectTo || `${window.location.origin}/dashboard`;

    useEffect(() => {
        if (resendCooldown <= 0) return;
        const timer = window.setInterval(() => {
            setResendCooldown((prev) => (prev > 0 ? prev - 1 : 0));
        }, 1000);
        return () => window.clearInterval(timer);
    }, [resendCooldown]);

    const normalizeEmail = (value: string) => value.trim().toLowerCase();

    const isValidEmail = (value: string) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value);

    const resetFeedback = () => {
        setError(null);
        setAuthMessage(null);
        setAuthMessageContext(null);
        setFieldErrors({});
    };

    const setSuccessMessage = (message: string, context: AuthMode) => {
        setAuthMessage(message);
        setAuthMessageContext(context);
        onSuccess?.(message);
    };

    const shouldMaskSignInError = (message: string) => {
        const lower = message.toLowerCase();
        return lower.includes('signups not allowed') || lower.includes('user not found');
    };

    const requestMagicLink = async (targetEmail: string, mode: AuthMode) => {
        if (mode === 'signin') {
            return supabase.auth.signInWithOtp({
                email: targetEmail,
                options: {
                    emailRedirectTo: redirectUrl,
                    shouldCreateUser: false
                }
            });
        }

        return supabase.auth.signInWithOtp({
            email: targetEmail,
            options: {
                emailRedirectTo: redirectUrl,
                data: {
                    terms_accepted: true,
                    marketing_opt_in: marketingOptIn,
                    terms_accepted_at: new Date().toISOString()
                }
            }
        });
    };

    const handleRegister = async (e: React.FormEvent) => {
        e.preventDefault();
        resetFeedback();

        const normalizedEmail = normalizeEmail(email);
        const nextErrors: FieldErrors = {};

        if (!normalizedEmail) {
            nextErrors.email = 'Email is required.';
        } else if (!isValidEmail(normalizedEmail)) {
            nextErrors.email = 'Enter a valid email address.';
        }

        if (!termsAccepted) {
            nextErrors.terms = 'You must agree to the Terms and Privacy Policy.';
        }

        if (!healthConsent) {
            nextErrors.healthConsent = 'Health data consent is required to use the app.';
        }

        if (Object.keys(nextErrors).length > 0) {
            setFieldErrors(nextErrors);
            if (nextErrors.email) {
                emailRegisterRef.current?.focus();
            }
            return;
        }

        setEmail(normalizedEmail);
        setLoading(true);

        try {
            const { error } = await requestMagicLink(normalizedEmail, 'register');

            if (error) throw error;
            setSuccessMessage(REGISTER_SUCCESS_MESSAGE, 'register');
            setResendCooldown(RESEND_COOLDOWN_SECONDS);
        } catch (err) {
            console.error('Registration error:', err);
            handleAuthError(err as Error, false);
        } finally {
            setLoading(false);
        }
    };

    const handleSignIn = async (e: React.FormEvent) => {
        e.preventDefault();
        resetFeedback();

        const normalizedEmail = normalizeEmail(email);
        const nextErrors: FieldErrors = {};

        if (!normalizedEmail) {
            nextErrors.email = 'Email is required.';
        } else if (!isValidEmail(normalizedEmail)) {
            nextErrors.email = 'Enter a valid email address.';
        }

        if (usePassword && !password) {
            nextErrors.password = 'Password is required.';
        }

        if (Object.keys(nextErrors).length > 0) {
            setFieldErrors(nextErrors);
            if (nextErrors.email) {
                emailSignInRef.current?.focus();
            } else if (nextErrors.password) {
                passwordRef.current?.focus();
            }
            return;
        }

        setEmail(normalizedEmail);
        setLoading(true);

        try {
            if (usePassword) {
                // Password Login
                const { error } = await supabase.auth.signInWithPassword({
                    email: normalizedEmail,
                    password
                });
                if (error) throw error;
                // Success is handled by onAuthStateChange in parent or redirect
            } else {
                // Magic Link Login
                const { error } = await requestMagicLink(normalizedEmail, 'signin');

                if (error) throw error;
                setSuccessMessage(GENERIC_SIGN_IN_MESSAGE, 'signin');
                setResendCooldown(RESEND_COOLDOWN_SECONDS);
            }
        } catch (err) {
            const authError = err as Error;
            console.error('Login error:', authError);
            if (!usePassword && shouldMaskSignInError(authError.message)) {
                setSuccessMessage(GENERIC_SIGN_IN_MESSAGE, 'signin');
                setResendCooldown(RESEND_COOLDOWN_SECONDS);
            } else {
                handleAuthError(authError, usePassword);
            }
        } finally {
            setLoading(false);
        }
    };

    const handleResend = async () => {
        if (!authMessageContext || resendCooldown > 0 || loading) return;

        const normalizedEmail = normalizeEmail(email);
        if (!isValidEmail(normalizedEmail)) {
            setAuthMessage(null);
            setAuthMessageContext(null);
            setFieldErrors({ email: 'Enter a valid email address to resend.' });
            setActiveTab(authMessageContext);
            if (authMessageContext === 'register') {
                emailRegisterRef.current?.focus();
            } else {
                emailSignInRef.current?.focus();
            }
            return;
        }

        setLoading(true);
        setError(null);
        try {
            const { error } = await requestMagicLink(normalizedEmail, authMessageContext);
            if (error) throw error;
            setResendCooldown(RESEND_COOLDOWN_SECONDS);
        } catch (err) {
            handleAuthError(err as Error, false);
        } finally {
            setLoading(false);
        }
    };

    const handleAuthError = (err: Error, isPasswordLogin: boolean) => {
        const lower = (err.message || '').toLowerCase();
        let message = err.message || 'Authentication failed. Please try again.';

        if (lower.includes('rate limit') || lower.includes('too many requests')) {
            message = "Too many attempts. Please wait a minute before trying again.";
        } else if (isPasswordLogin && (lower.includes('invalid login credentials') || lower.includes('invalid credentials'))) {
            message = "Invalid email or password.";
        } else if (!isPasswordLogin && lower.includes('invalid email')) {
            message = "Enter a valid email address.";
        }
        setError(message);
    };

    const handleTabChange = (value: string) => {
        const nextTab = value as AuthMode;
        setActiveTab(nextTab);
        setUsePassword(false);
        resetFeedback();
    };

    return (
        <Card className="border-slate-200/60 dark:border-slate-800 shadow-xl bg-white/80 dark:bg-slate-950/80 backdrop-blur-sm overflow-hidden border-none relative">
            {/* Background decoration */}
            <div className="absolute top-0 right-0 w-32 h-32 bg-blue-500/5 rounded-bl-[100px] -z-10" />

            <CardHeader className="space-y-4 text-center pb-2">
                <div className="mx-auto w-12 h-12 rounded-full bg-blue-100 dark:bg-blue-900/30 flex items-center justify-center text-blue-600 dark:text-blue-400">
                    <Mail className="w-6 h-6" />
                </div>
                <div className="space-y-2">
                    <CardTitle className="text-2xl font-bold tracking-tight">{title}</CardTitle>
                    <CardDescription className="text-base">
                        {description}
                    </CardDescription>
                </div>
            </CardHeader>
            <CardContent className="space-y-6 pt-2">
                {authMessage ? (
                    <div className="flex flex-col items-center space-y-4 py-4" role="status" aria-live="polite">
                        <div className="rounded-full bg-green-100 p-3 text-green-600 dark:bg-green-900/30 dark:text-green-400">
                            <CheckCircle className="h-8 w-8" aria-hidden="true" />
                        </div>
                        <div className="text-center space-y-2">
                            <h3 className="font-semibold text-lg">Check your inbox</h3>
                            <p className="text-muted-foreground text-sm max-w-xs mx-auto">
                                {authMessage}
                            </p>
                            <p className="text-xs text-muted-foreground">
                                Sent to <span className="font-medium text-foreground">{email}</span>
                            </p>
                            {authMessageContext === 'signin' && (
                                <p className="text-xs text-muted-foreground max-w-xs mx-auto">
                                    For security, we don&apos;t confirm whether an account exists.
                                </p>
                            )}
                        </div>
                        <div className="w-full space-y-2 mt-4">
                            <Button
                                type="button"
                                className="w-full"
                                variant="secondary"
                                onClick={handleResend}
                                disabled={loading || resendCooldown > 0}
                            >
                                {loading ? (
                                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                                ) : resendCooldown > 0 ? (
                                    `Resend in ${resendCooldown}s`
                                ) : (
                                    'Resend email'
                                )}
                            </Button>
                            <Button
                                type="button"
                                variant="outline"
                                className="w-full"
                                onClick={() => {
                                    setAuthMessage(null);
                                    setAuthMessageContext(null);
                                }}
                            >
                                Use different email
                            </Button>
                        </div>
                    </div>
                ) : (
                    <Tabs value={activeTab} onValueChange={handleTabChange} className="w-full">
                        <TabsList className="grid w-full grid-cols-2 mb-6">
                            <TabsTrigger value="signin">Sign In</TabsTrigger>
                            <TabsTrigger value="register">Join</TabsTrigger>
                        </TabsList>

                        <TabsContent value="signin">
                            <form onSubmit={handleSignIn} className="space-y-4">
                                <div className="space-y-2">
                                    <Label htmlFor="email-signin">Email Address</Label>
                                    <Input
                                        id="email-signin"
                                        name="email"
                                        type="email"
                                        ref={emailSignInRef}
                                        autoComplete="username"
                                        autoCapitalize="none"
                                        autoCorrect="off"
                                        placeholder="coach@example.com"
                                        value={email}
                                        inputMode="email"
                                        spellCheck={false}
                                        onChange={(e) => {
                                            setEmail(e.target.value);
                                            if (fieldErrors.email) {
                                                setFieldErrors((prev) => ({ ...prev, email: undefined }));
                                            }
                                            if (error) setError(null);
                                        }}
                                        aria-invalid={!!fieldErrors.email}
                                        aria-describedby={fieldErrors.email ? 'signin-email-error' : undefined}
                                        required
                                        className="h-11 bg-white dark:bg-slate-950"
                                        disabled={loading}
                                    />
                                    {fieldErrors.email && (
                                        <p id="signin-email-error" className="text-xs text-destructive" role="alert">
                                            {fieldErrors.email}
                                        </p>
                                    )}
                                </div>

                                {usePassword && (
                                    <div className="space-y-2">
                                        <div className="flex items-center justify-between">
                                            <Label htmlFor="password-signin">Password</Label>
                                            <Button
                                                variant="link"
                                                className="p-0 h-auto text-xs text-blue-600"
                                                onClick={(e) => {
                                                    e.preventDefault();
                                                    setUsePassword(false);
                                                    setError(null);
                                                    setFieldErrors((prev) => ({ ...prev, password: undefined }));
                                                }}
                                            >
                                                Forgot? Use Magic Link
                                            </Button>
                                        </div>
                                        <Input
                                            id="password-signin"
                                            name="password"
                                            type="password"
                                            ref={passwordRef}
                                            autoComplete="current-password"
                                            placeholder="••••••••"
                                            value={password}
                                            onChange={(e) => {
                                                setPassword(e.target.value);
                                                if (fieldErrors.password) {
                                                    setFieldErrors((prev) => ({ ...prev, password: undefined }));
                                                }
                                                if (error) setError(null);
                                            }}
                                            aria-invalid={!!fieldErrors.password}
                                            aria-describedby={fieldErrors.password ? 'signin-password-error' : undefined}
                                            required
                                            className="h-11 bg-white dark:bg-slate-950"
                                            disabled={loading}
                                        />
                                        {fieldErrors.password && (
                                            <p id="signin-password-error" className="text-xs text-destructive" role="alert">
                                                {fieldErrors.password}
                                            </p>
                                        )}
                                    </div>
                                )}

                                {error && (
                                    <Alert variant="destructive" className="py-2" aria-live="assertive">
                                        <AlertTriangle className="h-4 w-4" />
                                        <AlertTitle className="text-sm font-semibold">Login Failed</AlertTitle>
                                        <AlertDescription className="text-xs mt-1">{error}</AlertDescription>
                                    </Alert>
                                )}

                                <Button
                                    type="submit"
                                    className="w-full h-11 text-base font-medium shadow-lg shadow-blue-500/20"
                                    disabled={loading || !email.trim() || (usePassword && !password)}
                                    aria-busy={loading}
                                >
                                    {loading ? (
                                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                                    ) : (
                                        usePassword ? 'Log In with Password' : 'Log In with Email'
                                    )}
                                </Button>

                                <div className="relative">
                                    <div className="absolute inset-0 flex items-center">
                                        <span className="w-full border-t border-slate-200 dark:border-slate-800" />
                                    </div>
                                    <div className="relative flex justify-center text-xs uppercase">
                                        <span className="bg-white dark:bg-slate-950 px-2 text-muted-foreground">Or</span>
                                    </div>
                                </div>

                                <Button
                                    variant="outline"
                                    type="button"
                                    className="w-full"
                                    onClick={() => {
                                        setUsePassword(!usePassword);
                                        setError(null);
                                        setFieldErrors({});
                                    }}
                                >
                                    {usePassword ? (
                                        <>
                                            <Mail className="w-4 h-4 mr-2" />
                                            Use Magic Link instead
                                        </>
                                    ) : (
                                        <>
                                            <KeyRound className="w-4 h-4 mr-2" />
                                            Use Password instead
                                        </>
                                    )}
                                </Button>
                            </form>
                        </TabsContent>

                        <TabsContent value="register">
                            <form onSubmit={handleRegister} className="space-y-4">
                                <div className="space-y-2">
                                    <Label htmlFor="email-register">Email Address</Label>
                                    <Input
                                        id="email-register"
                                        name="email"
                                        type="email"
                                        ref={emailRegisterRef}
                                        autoComplete="username"
                                        autoCapitalize="none"
                                        autoCorrect="off"
                                        placeholder="you@active.com"
                                        value={email}
                                        inputMode="email"
                                        spellCheck={false}
                                        onChange={(e) => {
                                            setEmail(e.target.value);
                                            if (fieldErrors.email) {
                                                setFieldErrors((prev) => ({ ...prev, email: undefined }));
                                            }
                                            if (error) setError(null);
                                        }}
                                        aria-invalid={!!fieldErrors.email}
                                        aria-describedby={fieldErrors.email ? 'register-email-error' : undefined}
                                        required
                                        className="h-11 bg-white dark:bg-slate-950"
                                        disabled={loading}
                                    />
                                    {fieldErrors.email && (
                                        <p id="register-email-error" className="text-xs text-destructive" role="alert">
                                            {fieldErrors.email}
                                        </p>
                                    )}
                                    <p className="text-[10px] text-muted-foreground">Used for your training plan and account recovery.</p>
                                </div>

                                <div className="space-y-4 p-4 bg-slate-50 dark:bg-slate-900 rounded-lg border border-slate-100 dark:border-slate-800">
                                    <div className="flex items-start space-x-3">
                                        <Checkbox
                                            id="terms"
                                            checked={termsAccepted}
                                            onCheckedChange={(c) => setTermsAccepted(c as boolean)}
                                        />
                                        <div className="grid gap-1.5 leading-none">
                                            <label
                                                htmlFor="terms"
                                                className="text-sm font-medium leading-none cursor-pointer"
                                            >
                                                I agree to the <Link to="/terms" className="text-blue-600 hover:underline" target="_blank">Terms</Link>, <Link to="/privacy" className="text-blue-600 hover:underline" target="_blank">Privacy Policy</Link>, and <Link to="/cookie-policy" className="text-blue-600 hover:underline" target="_blank">Cookie Policy</Link>
                                            </label>
                                            <p className="text-[10px] text-muted-foreground">
                                                I assume full responsibility for my health and safety.
                                            </p>
                                            {fieldErrors.terms && (
                                                <p className="text-[11px] text-destructive" role="alert">
                                                    {fieldErrors.terms}
                                                </p>
                                            )}
                                        </div>
                                    </div>

                                    <div className="flex items-start space-x-3">
                                        <Checkbox
                                            id="marketing"
                                            checked={marketingOptIn}
                                            onCheckedChange={(c) => setMarketingOptIn(c as boolean)}
                                        />
                                        <div className="grid gap-1.5 leading-none">
                                            <label
                                                htmlFor="marketing"
                                                className="text-sm font-medium leading-none cursor-pointer"
                                            >
                                                (Optional) Keep me updated
                                            </label>
                                            <p className="text-[10px] text-muted-foreground">
                                                Occasional training tips and product updates. No spam.
                                            </p>
                                        </div>
                                    </div>

                                    <div className="flex items-start space-x-3">
                                        <Checkbox
                                            id="health-consent"
                                            checked={healthConsent}
                                            onCheckedChange={(c) => setHealthConsent(c as boolean)}
                                        />
                                        <div className="grid gap-1.5 leading-none">
                                            <label
                                                htmlFor="health-consent"
                                                className="text-sm font-medium leading-none cursor-pointer"
                                            >
                                                I consent to processing my health data
                                            </label>
                                            <p className="text-[10px] text-muted-foreground">
                                                Required for training analysis. We process heart rate, weight, and activity logs.
                                            </p>
                                            {fieldErrors.healthConsent && (
                                                <p className="text-[11px] text-destructive" role="alert">
                                                    {fieldErrors.healthConsent}
                                                </p>
                                            )}
                                        </div>
                                    </div>
                                </div>

                                {error && (
                                    <Alert variant="destructive" className="py-2" aria-live="assertive">
                                        <AlertTriangle className="h-4 w-4" />
                                        <AlertTitle className="text-sm font-semibold">Registration Failed</AlertTitle>
                                        <AlertDescription className="text-xs mt-1">{error}</AlertDescription>
                                    </Alert>
                                )}

                                <Button
                                    type="submit"
                                    className="w-full h-11 text-base font-medium shadow-lg shadow-indigo-500/20 bg-indigo-600 hover:bg-indigo-700 text-white"
                                    disabled={loading || !email.trim() || !termsAccepted || !healthConsent}
                                    aria-busy={loading}
                                >
                                    {loading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : 'Create Account'}
                                </Button>
                            </form>
                        </TabsContent>
                    </Tabs>
                )}
            </CardContent>
            <CardFooter className="flex flex-col space-y-4 border-t bg-slate-50/50 dark:bg-slate-900/50 p-6">
                <p className="text-xs text-center text-muted-foreground">
                    Secure passwordless access. We never share your data.
                </p>
            </CardFooter>
        </Card>
    );
}
